// @flow

import * as React from 'react';
import Box from 'sail/Box';
import {BodyMono, CaptionAltMono} from 'sail/Text';
import Icon from 'sail/Icon';

import {TerminalPrompt} from './input';
import {useStripeClui} from '../helpers/react/provider';
import WelcomePrompt from '../components/WelcomePrompt';

type Props = {
  handleKeyDown: (SyntheticKeyboardEvent<HTMLInputElement>) => ?boolean,
  paneIdx: number,
  cliInput: React.Node,
  suggestions: React.Node,
};

const StripeCluiTerminalPane = ({
  handleKeyDown,
  suggestions,
  cliInput,
  paneIdx,
}: Props): React.Node => {
  const {
    state: {isFocused, panes, focusedPane, locked},
    focusPane,
    removePane,
  } = useStripeClui();

  const {history, showWelcomePrompt} = panes[paneIdx];

  const cluiTerminalRef = React.useRef(null);

  React.useEffect(() => {
    if (isFocused && focusedPane === paneIdx && cluiTerminalRef.current) {
      cluiTerminalRef.current.focus();
    }
  }, [isFocused, focusedPane, paneIdx]);

  const handleFocus = () => focusPane(paneIdx);

  const paneTitle =
    history[history.length - 1]?.command
      ?.split(' ')
      .slice(0, 2)
      .join(' ') || 'stripe';

  return (
    <div
      className="StripeCluiTerminalPaneWrapper"
      style={{
        opacity: isFocused ? '1' : '0.6',
      }}
    >
      {panes.length > 1 && (
        <div className="StripeCluiTerminalPaneHeader">
          <button
            className="StripeCluiTerminalPaneButton"
            style={{
              color: focusedPane === paneIdx ? 'inherit' : 'rgb(164, 205, 254)',
              background:
                focusedPane === paneIdx ? 'rgb(164, 205, 254)' : 'transparent',
              minHeight: 22,
            }}
            onClick={() => removePane(paneIdx)}
            type="button"
            disabled={locked}
          >
            {!locked && (
              <Icon
                icon="cancel"
                color="inherit"
                size={12}
                style={{marginRight: 8}}
              />
            )}
            <CaptionAltMono color="inherit" style={{lineHeight: 0}}>
              {paneTitle}
            </CaptionAltMono>
          </button>
        </div>
      )}
      <div
        className="StripeCluiTerminalPane"
        onClick={handleFocus}
        tabIndex={paneIdx + 1}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        aria-label="Stripe CLI"
        role="textbox"
        ref={cluiTerminalRef}
      >
        <Box
          background={'blue800'}
          padding={{horizontal: 24, top: 12, bottom: 24}}
          flex={{
            direction: 'column',
            alignItems: 'flex-start',
            justifyContent: 'flex-start',
          }}
          style={{minHeight: '100%'}}
        >
          {showWelcomePrompt && (
            <Box padding={{bottom: 24}}>
              <WelcomePrompt />
            </Box>
          )}
          {history.map((commandOutput, idx) => {
            if (commandOutput.command.includes('stripe listen')) {
              return (
                <React.Fragment key={idx}>
                  <pre>
                    <code>
                      <BodyMono color="gray200" wordBreak="word">
                        {<TerminalPrompt />} {commandOutput.command} {'\n'}
                      </BodyMono>
                    </code>
                  </pre>

                  {commandOutput.output}
                </React.Fragment>
              );
            } else {
              return (
                <React.Fragment key={idx}>
                  <pre>
                    <code>
                      <BodyMono color="gray200" wordBreak="word">
                        {<TerminalPrompt />} {commandOutput.command} {'\n'}
                      </BodyMono>

                      <pre>
                        <code className={'CodeBlock'}>
                          {commandOutput.output}
                        </code>
                      </pre>
                    </code>
                  </pre>
                </React.Fragment>
              );
            }
          })}
          {cliInput}
          {focusedPane === paneIdx && suggestions}
        </Box>
      </div>
    </div>
  );
};

export default StripeCluiTerminalPane;
